import PropTypes from 'prop-types';

import {
  Box, Flex, Heading, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import Link from '@salesforce/retail-react-app/app/components/link';

import {Component} from '@amblique/king-living/app/components/contentful/component';

/**
 * BlogSummary - Contentful component for displaying blog summary cards
 * Auto-fetches full Blog data if component is incomplete using CONTENTFUL_METADATA
 *
 * @param {Object} props - Component props
 * @param {Object} props.component - Contentful Blog data
 * @param {Object} props.component.blogTileImage - Blog tile image component
 * @param {string} props.component.blogCategory - Blog category text
 * @param {string} props.component.title - Blog title
 * @param {string[]} props.component.tags - Blog tags array
 * @param {string} props.component.url - URL to link to (applied to image)
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @param {string} [props.variant] - Style variant ('featured' for horizontal layout)
 * @returns {React.ReactElement}
 */

export const BlogSummary = ({component, isComplete: isCompleteProp, isFetching: isFetchingProp = false, variant, ...props}) => {
  const {blogTileImage, blogCategory, title, contentfulMetadata, url} = component;
  // Extract tags from Contentful metadata
  const tags = contentfulMetadata?.tags || [];
  const styles = useMultiStyleConfig('contentful/blog-summary', {component: component, variant});

  return (
    <Box {...styles.container} {...props}>
      {/* Blog Tile Image - wrapped with Link if url exists */}
      {blogTileImage && (
        <Box {...styles.imageContainer}>
          {url ? (
            <Link to={url} {...styles.imageLink}>
              <Component component={blogTileImage} {...styles.image} />
            </Link>
          ) : (
            <Component component={blogTileImage} {...styles.image} />
          )}
        </Box>
      )}

      {/* Blog Content */}
      <Box {...styles.content}>
        {blogCategory && (
          <Text {...styles.category}>{blogCategory}</Text>
        )}
        {title && (
          <Heading {...styles.title}>{title}</Heading>
        )}
      </Box>

      {/* Tags from Contentful metadata */}
      {tags && tags.length > 0 && (
        <Flex {...styles.tagsContainer}>
          {tags.map((tag) => (
            <Box key={tag.id} {...styles.tag}>
              <Text {...styles.tagText}>{tag.name}</Text>
            </Box>
          ))}
        </Flex>
      )}
    </Box>
  );
};

BlogSummary.displayName = 'BlogSummary';
BlogSummary.propTypes = {
  component: PropTypes.shape({
    __typename: PropTypes.string,
    sys: PropTypes.shape({
      id: PropTypes.string,
    }),
    blogTileImage: PropTypes.object,
    blogCategory: PropTypes.string,
    title: PropTypes.string,
    contentfulMetadata: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })),
    }),
    url: PropTypes.string,
  }).isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
  variant: PropTypes.string,
};
